import React, { useState } from 'react';
import { db } from '../db';
import { apiClient } from '../api';
import { Loader2, Sparkles } from 'lucide-react';
import GenerationAlert from '../components/GenerationAlert';
import { useGenerationAlert } from '../hooks/useGenerationAlert';

const Single = () => {
    const [prompt, setPrompt] = useState('');
    const [aspectRatio, setAspectRatio] = useState('portrait');
    const [isGenerating, setIsGenerating] = useState(false);
    const { alertState, showSuccessAlert, showErrorAlert, hideAlert, checkAuthorizationBeforeGeneration } = useGenerationAlert();

    const handleGenerate = async (e: React.FormEvent) => {
        e.preventDefault();

        // Check authorization before proceeding
        if (!checkAuthorizationBeforeGeneration()) {
            return; // Alert will be shown by the hook
        }

        setIsGenerating(true);

        try {
            const data = await apiClient.generate(prompt, aspectRatio);

            await db.addGeneration({
                id: data.id,
                batchId: data.batchId,
                prompt,
                aspectRatio,
                mode: 'single',
                createdAt: Date.now(),
                status: 'queued'
            });

            setPrompt('');
            showSuccessAlert('Video generation started successfully!');

        } catch (err) {
            console.error(err);
            const errorMessage = err instanceof Error ? err.message : 'Error generating video';
            showErrorAlert(errorMessage);
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="page-container">
            <header className="page-header">
                <h2>Single Generation</h2>
                <p>Create a unique video from a text prompt.</p>
            </header>

            <div className="content-card">
                <form onSubmit={handleGenerate}>
                    <div className="form-group">
                        <label htmlFor="prompt">Prompt</label>
                        <textarea
                            id="prompt"
                            value={prompt}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setPrompt(e.target.value)}
                            rows={5}
                            placeholder="Describe your video in detail..."
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="aspectRatio">Aspect Ratio</label>
                        <div className="select-wrapper">
                            <select
                                id="aspectRatio"
                                value={aspectRatio}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAspectRatio(e.target.value)}
                            >
                                <option value="landscape">Landscape (16:9)</option>
                                <option value="portrait">Portrait (9:16)</option>
                                <option value="square">Square (1:1)</option>
                            </select>
                        </div>
                    </div>

                    <button type="submit" disabled={isGenerating} className="btn-primary">
                        {isGenerating ? (
                            <>
                                <Loader2 className="animate-spin" size={20} />
                                <span>Generating...</span>
                            </>
                        ) : (
                            <>
                                <Sparkles size={20} />
                                <span>Generate Video</span>
                            </>
                        )}
                    </button>
                </form>
            </div>

            <GenerationAlert
                isVisible={alertState.isVisible}
                type={alertState.type}
                title={alertState.title}
                message={alertState.message}
                onClose={hideAlert}
            />
        </div>
    );
};

export default Single;
